import type { IgAccountOverview } from "@ml/shared";
import { Badge, Button, Callout, Field, Input, ProgressBar, Skeleton, useToast } from "@ml/ui";
import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router";
import { AddIgAccountGuide, ADD_ACCOUNT_ANCHOR } from "../../components/accounts/AddIgAccountGuide";
import { IgAccountCard } from "../../components/accounts/IgAccountCard";
import { InboxPrivacyCard } from "../../components/InboxPrivacyCard";
import { NoIgAccount } from "../../components/NoIgAccount";
import { isApiError } from "../../lib/api";
import { useUser } from "../../lib/auth";
import { useIgAccount, useIgAccountsOverview } from "../../lib/igAccount";
import { accountRunState, RUN_STATE_LABELS, type AccountRunState } from '../../lib/accountStatus';

/** Settings → LinkedIn accounts: every connected account (workspace), plan usage, how to add one. */
export function XTab() {
  const user = useUser();
  const toast = useToast();
  const { accounts, isLoading, current, switchAccount, error, refetch } = useIgAccount();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<AccountRunState | ''>('');
  const overview = useIgAccountsOverview({ enabled: accounts.length > 0 });
  const { hash } = useLocation();

  const byId = useMemo(() => new Map<string, IgAccountOverview>((overview.data?.items ?? []).map((o) => [o.account.id, o])), [overview.data]);
  const visibleAccounts = accounts.filter(a => {
    const account = byId.get(a.id)?.account ?? a;
    return (!status || accountRunState(account) === status) &&
      [a.username, a.fullName, account.driver?.label].some(value => (value ?? '').toLowerCase().includes(search.trim().toLowerCase().replace(/^@/, '')));
  });

  // "Add an account" in the switcher links to #add-account.
  useEffect(() => {
    if (isLoading || hash !== `#${ADD_ACCOUNT_ANCHOR}`) return;
    document.getElementById(ADD_ACCOUNT_ANCHOR)?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [hash, isLoading]);

  if (isLoading) {
    return (
      <div className="gf-stack">
        <Skeleton height={140} radius={10} />
        <Skeleton height={140} radius={10} />
      </div>
    );
  }
  if (accounts.length === 0) {
    return (
      <div className="gf-stack">
        {error ? <Callout tone="danger" title="Could not load your LinkedIn accounts" action={<Button onClick={refetch}>Try again</Button>}>Your account list could not be refreshed.</Callout> : null}
        <NoIgAccount compact />
        <AddIgAccountGuide used={0} limit={user.igAccountLimit} />
        <InboxPrivacyCard />
      </div>
    );
  }

  const limit = overview.data?.limit ?? user.igAccountLimit;
  const used = overview.data?.used ?? accounts.length;
  const unlimited = user.plan === "lifetime";
  const atLimit = !unlimited && used >= limit;
  // An older API without /overview answers 404: show the accounts without today's numbers.
  const overviewFailed = overview.isError && !isApiError(overview.error, 404);

  return (
    <div className="gf-stack">
      <div className="gfp-ws-usage">
        {unlimited ? <p><strong>{used} connected LinkedIn accounts · Unlimited included</strong></p> : <ProgressBar
          value={Math.min(used, limit)}
          max={Math.max(limit, 1)}
          size="sm"
          label={
            <span>
              <b className="gfp-strong">
                {used} of {limit}
              </b>{" "}
              LinkedIn {limit === 1 ? "account" : "accounts"} on your plan
            </span>
          }
          valueLabel={
            atLimit ? (
              <a href={`#${ADD_ACCOUNT_ANCHOR}`} className="gfp-ws-usage__limit">
                <Badge size="sm" tone="warn">
                  At your limit
                </Badge>
              </a>
            ) : (
              `${limit - used} free`
            )
          }
        />}
        <p className="gf-muted gfp-ws-usage__hint">
          Every account is its own workspace: its own settings, brand voice, campaigns, contacts and history. Switch between
          them at the top of any page. Save your edits first; switching closes the previous account’s unsaved editors.
          Your selection changes the workspace, while each Chrome profile stays signed in to its own LinkedIn account.
        </p>
      </div>

      {overviewFailed ? (
        <Callout tone="warn" title="Couldn't load today's numbers" action={<Button size="sm" onClick={() => void overview.refetch()}>Try again</Button>}>
          {overview.error instanceof Error ? overview.error.message : null}
        </Callout>
      ) : null}

      <div className="gfp-ws-filters">
        <Field label="Find a LinkedIn account"><Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Handle, name or browser profile" /></Field>
        <Field label="Browser status"><select aria-label="Browser status" value={status} onChange={e => setStatus(e.target.value as AccountRunState | '')}><option value="">All statuses</option>{Object.entries(RUN_STATE_LABELS).map(([value,label]) => <option key={value} value={value}>{label}</option>)}</select></Field>
        <Button variant="secondary" onClick={() => { refetch(); void overview.refetch(); }}>Refresh accounts</Button>
      </div>
      <p role="status" className="gf-muted">Showing {visibleAccounts.length} of {accounts.length} connected LinkedIn accounts.</p>
      {!visibleAccounts.length ? <Callout tone="neutral">No accounts match these filters. <Button variant="ghost" onClick={() => { setSearch(''); setStatus(''); }}>Clear filters</Button></Callout> : null}
      <ul className="gfp-iglist">
        {visibleAccounts.map((a) => {
          const o = byId.get(a.id) ?? null;
          return (
            <IgAccountCard
              key={a.id}
              // The overview row is fetched separately and may be fresher (driver, engine status).
              account={o?.account ?? a}
              overview={o}
              isCurrent={a.id === current?.id}
              onSwitch={() => {
                switchAccount(a.id);
                toast.success(`Switched to @${a.username}`, "This workspace now shows that account. LinkedIn browser logins stay unchanged.");
              }}
            />
          );
        })}
      </ul>

      <AddIgAccountGuide used={used} limit={limit} />
      <InboxPrivacyCard />
    </div>
  );
}
